// apps/hub/src/components/HubLayout.tsx
import { useAuthStore, UserMenu } from '@neuroforge/shared/client'
import { LoadingSpinner, Button, ThemeToggle, AppSwitcher, Logo } from '@neuroforge/ui'
import { Suspense, useState, useEffect } from 'react'
import { Outlet, Link, useLocation, ScrollRestoration } from 'react-router'

import { EXTERNAL_LINKS, SOCIAL_LINKS } from '../config/links'

import BetaBadge from './BetaBadge'

export default function HubLayout() {
  const { isAuthenticated } = useAuthStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const { pathname } = useLocation()
  const currentYear = new Date().getFullYear()
  const isWizardPage = pathname === '/ai-wizard' || pathname === '/quiz'

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  return (
    <div className="bg-background text-foreground min-h-screen transition-colors duration-300">
      <nav className="nav bg-background/80 sticky top-0 z-50 border-b backdrop-blur-md">
        <div className="wide nav-inner mx-auto flex max-w-7xl items-center justify-between p-4">
          {/* Switcher global entre les apps NeuroForge */}
          <AppSwitcher current="hub" />

          {/* Desktop Nav Links */}
          <div className="nav-links hidden items-center gap-6 md:flex">
            <Link
              to="/"
              className={[
                'hover:text-primary transition-colors',
                pathname === '/' ? 'text-primary font-medium' : '',
              ].join(' ')}
              aria-current={pathname === '/' ? 'page' : undefined}
            >
              Accueil
            </Link>
            {/*<Link*/}
            {/*  to="/labs"*/}
            {/*  className={[*/}
            {/*    'hover:text-primary transition-colors',*/}
            {/*    pathname === '/labs' ? 'text-primary font-medium' : '',*/}
            {/*  ].join(' ')}*/}
            {/*  aria-current={pathname === '/labs' ? 'page' : undefined}*/}
            {/*>*/}
            {/*  Labs*/}
            {/*</Link>*/}
            <Link
              to="/ai-wizard"
              className={[
                'nav-wizard-cta flex items-center gap-1 transition-colors',
                isWizardPage ? 'is-active' : '',
              ].join(' ')}
              aria-current={isWizardPage ? 'page' : undefined}
            >
              Diagnostic Stack IA <BetaBadge />
            </Link>
            <div className="ml-4 flex items-center gap-4">
              <ThemeToggle />
              {isAuthenticated ? (
                <UserMenu />
              ) : (
                <>
                  <Link
                    to="/auth/login"
                    className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                  >
                    Connexion
                  </Link>
                  <Link to="/auth/register">
                    <Button className="nav-cta">S'inscrire</Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile: theme toggle + hamburger */}
          <div className="flex items-center gap-3 md:hidden">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="mobile-menu-btn"
              aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={mobileMenuOpen}
            >
              <span className={`hamburger-line ${mobileMenuOpen ? 'hamburger-open' : ''}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-nav md:hidden">
            <Link
              to="/ai-wizard"
              className="mobile-wizard-cta flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              🧙 Trouver ma stack IA <BetaBadge />
            </Link>
            <Link to="/labs" onClick={() => setMobileMenuOpen(false)}>
              Labs
            </Link>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              Contact
            </Link>
            <div className="mobile-nav-cta">
              {isAuthenticated ? (
                <Link to="/profile" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="secondary" size="sm" className="w-full">
                    Mon Profil
                  </Button>
                </Link>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link to="/auth/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="nav-cta w-full">S'inscrire</Button>
                  </Link>
                  <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="secondary" size="sm" className="w-full">
                      Connexion
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      <main className="relative z-10">
        <Suspense
          fallback={
            <div className="flex h-[50vh] items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
      <ScrollRestoration />

      <footer className="mt-20 border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Grille de liens */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {/* Plateforme */}
            <nav aria-label="Plateforme">
              <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">
                Plateforme
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href={EXTERNAL_LINKS.academy}>
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Academy
                    </span>
                  </a>
                </li>
                <li>
                  <a href={EXTERNAL_LINKS.prompts}>
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Prompts
                    </span>
                  </a>
                </li>
                <li>
                  <a href={EXTERNAL_LINKS.forge}>
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      La Forge
                    </span>
                  </a>
                </li>
              </ul>
            </nav>

            {/* Ressources */}
            <nav aria-label="Ressources">
              <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">
                Ressources
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/ai-wizard">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Diagnostic Stack IA
                    </span>
                  </Link>
                </li>
                {/*<li>*/}
                {/*  <Link to="/labs">*/}
                {/*    <span*/}
                {/*      className={*/}
                {/*        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'*/}
                {/*      }*/}
                {/*    >*/}
                {/*      Labs*/}
                {/*    </span>*/}
                {/*  </Link>*/}
                {/*</li>*/}
                <li>
                  <Link to="/contact">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Contact
                    </span>
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Communauté */}
            <nav aria-label="Communauté">
              <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">
                Communauté
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href={SOCIAL_LINKS.discord} target="_blank" rel="noopener noreferrer">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Discord
                    </span>
                  </a>
                </li>
                <li>
                  <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Facebook
                    </span>
                  </a>
                </li>
                <li>
                  <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      TikTok
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href={SOCIAL_LINKS.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 transition-colors hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    YouTube
                  </a>
                </li>
              </ul>
            </nav>

            {/* Légal */}
            <nav aria-label="Légal">
              <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Légal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/mentions-legales">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Mentions légales
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to="/cgv">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      CGU &amp; CGV
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to="/confidentialite">
                    <span
                      className={
                        'text-slate-500 transition-colors hover:text-slate-950 dark:text-slate-400 dark:hover:text-white'
                      }
                    >
                      Confidentialité
                    </span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Séparateur + bas de page */}
          <div className="mt-10 flex flex-col items-center justify-between gap-6 border-t border-slate-200 pt-8 sm:flex-row dark:border-slate-800">
            {/* Logo + copyright */}
            <div className="flex items-center gap-3">
              <Logo size={24} />
              <div className="text-sm text-slate-500 dark:text-slate-400">
                <span className="font-semibold text-slate-700 dark:text-slate-300">NeuroForge</span>
                <span className="mx-1.5">·</span>
                <span>© {currentYear} MADSAAS</span>
              </div>
            </div>

            {/* Icônes sociales */}
            <div className="flex items-center gap-3">
              <a
                href={SOCIAL_LINKS.discord}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Discord"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-purple-100 hover:text-purple-600 dark:text-slate-500 dark:hover:bg-purple-900/30 dark:hover:text-purple-400"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
              </a>
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-purple-100 hover:text-purple-600 dark:text-slate-500 dark:hover:bg-purple-900/30 dark:hover:text-purple-400"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href={SOCIAL_LINKS.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-purple-100 hover:text-purple-600 dark:text-slate-500 dark:hover:bg-purple-900/30 dark:hover:text-purple-400"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                </svg>
              </a>
              <a
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-purple-100 hover:text-purple-600 dark:text-slate-500 dark:hover:bg-purple-900/30 dark:hover:text-purple-400"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Tagline */}
          <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
            Comprendre l’IA, la pratiquer, avancer ensemble.
          </p>
        </div>
      </footer>
    </div>
  )
}
